const DAY_NAME_MAP: Record<number, string> = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
};

export interface PackageAvailabilityCalendar {
  defaultAvailability: boolean;
  availableDates: string[];
  nonAvailableDates: string[];
}

export interface PackageAvailabilitySource {
  defaultAvailability?: boolean;
  availableDates?: string[];
  nonAvailableDates?: string[];
  /** @deprecated use nonAvailableDates */
  nonAvailabilityDates?: string[];
}

/**
 * Resolves package calendar fields from API response (supports legacy field names).
 *
 * Mode rules:
 * - Available Dates: defaultAvailability === false OR availableDates has items
 * - Unavailable Dates: defaultAvailability === true OR nonAvailableDates has items
 * - Both empty / no defaultAvailability → Unavailable Dates (default)
 */
export function resolvePackageAvailabilityCalendar(
  source: PackageAvailabilitySource | null | undefined
): PackageAvailabilityCalendar {
  const availableDates = source?.availableDates ?? [];
  const nonAvailableDates =
    source?.nonAvailableDates ?? source?.nonAvailabilityDates ?? [];

  const isAvailableDatesMode =
    source?.defaultAvailability === false || availableDates.length > 0;

  if (isAvailableDatesMode) {
    return {
      defaultAvailability: false,
      availableDates,
      nonAvailableDates: [],
    };
  }

  return {
    defaultAvailability: true,
    availableDates: [],
    nonAvailableDates,
  };
}

/** True when the package uses a fixed list of available dates (dropdown flow). */
export function isPackageAvailableDatesMode(
  calendar: PackageAvailabilityCalendar | null | undefined
): boolean {
  return (
    !!calendar &&
    !calendar.defaultAvailability &&
    calendar.availableDates.length > 0
  );
}

export function parsePackageDateString(dateStr: string): Date {
  if (!dateStr) return new Date(NaN);

  // Support YYYY-MM-DD and ISO datetime strings (e.g. 2026-07-15T00:00:00.000Z)
  const dateOnly = dateStr.includes('T')
    ? dateStr.split('T')[0]
    : dateStr.split(' ')[0];
  const [year, month, day] = dateOnly.split('-').map(Number);

  if (!year || !month || !day) return new Date(NaN);

  return new Date(year, month - 1, day);
}

export function isValidPackageDate(date: Date): boolean {
  return !Number.isNaN(date.getTime());
}

export function formatPackageDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function startOfLocalDay(date: Date = new Date()): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function isPastPackageDate(date: Date, today: Date): boolean {
  return startOfLocalDay(date).getTime() < startOfLocalDay(today).getTime();
}

/** First configured available date on or after today, or null if none. */
export function getNearestFutureAvailableDate(
  availableDates: string[],
  today: Date = startOfLocalDay()
): Date | null {
  const nearest = [...availableDates]
    .map(parsePackageDateString)
    .filter(date => isValidPackageDate(date) && !isPastPackageDate(date, today))
    .sort((a, b) => a.getTime() - b.getTime())[0];

  return nearest ?? null;
}

export interface BookingAvailabilityOptions {
  defaultAvailability?: boolean;
  availableDates?: string[];
  nonAvailableDates?: string[];
  nonAvailabilityDays?: string[];
}

function isDateInList(date: Date, dates: string[]): boolean {
  const dateStr = formatPackageDateString(date);
  return dates.some(d => {
    const parsed = parsePackageDateString(d);
    return (
      isValidPackageDate(parsed) && formatPackageDateString(parsed) === dateStr
    );
  });
}

function isDayOfWeekBlocked(
  date: Date,
  nonAvailabilityDays: string[]
): boolean {
  const dayName = DAY_NAME_MAP[date.getDay()];
  const disabled = new Set(
    nonAvailabilityDays.map(d => d.toLowerCase().trim())
  );
  return disabled.has(dayName);
}

/**
 * Nearest bookable date on or after today.
 * - Available-dates mode (`defaultAvailability === false`): first future date in the list
 * - Unavailable-dates mode: first day not blocked by non-available dates/days
 */
export function getNearestAvailableBookingDate(
  options: BookingAvailabilityOptions,
  today: Date = startOfLocalDay(),
  maxDaysAhead = 366
): Date | null {
  const defaultAvailability = options.defaultAvailability ?? true;
  const availableDates = options.availableDates ?? [];
  const nonAvailableDates = options.nonAvailableDates ?? [];
  const nonAvailabilityDays = options.nonAvailabilityDays ?? [];

  if (!defaultAvailability) {
    return getNearestFutureAvailableDate(availableDates, today);
  }

  for (let offset = 0; offset <= maxDaysAhead; offset++) {
    const candidate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + offset
    );
    if (isDayOfWeekBlocked(candidate, nonAvailabilityDays)) continue;
    if (isDateInList(candidate, nonAvailableDates)) continue;
    return candidate;
  }

  return null;
}

export function getPackageDatePricingStatus(
  date: Date,
  weekendDays: string[] = [],
  specialDates: string[] = []
): 'special' | 'weekend' | 'allowed' {
  const dateStr = formatPackageDateString(date);
  if (specialDates.includes(dateStr)) return 'special';

  const dayName = DAY_NAME_MAP[date.getDay()];
  const weekendSet = new Set(weekendDays.map(d => d.toLowerCase().trim()));
  if (weekendSet.has(dayName)) return 'weekend';

  return 'allowed';
}
